export const ANNOUNCEMENT_SURFACES = [
  'global',
  'assetDetails',
  'canvas',
  'canvasNullState',
  'canvasChatComposer',
  'boards',
  'plans',
] as const;

export type AnnouncementSurface = (typeof ANNOUNCEMENT_SURFACES)[number];

/** Surfaces whose shape is set by the app's layout, so the stored variant has no effect there. */
export const ANNOUNCEMENT_FIXED_SHAPE_SURFACES = ['global', 'canvasChatComposer'] as const;

export type AnnouncementFixedShapeSurface = (typeof ANNOUNCEMENT_FIXED_SHAPE_SURFACES)[number];

export type AnnouncementConfigurableShapeSurface = Exclude<AnnouncementSurface, AnnouncementFixedShapeSurface>;

export const ANNOUNCEMENT_FIXED_SHAPE_DESCRIPTIONS: Record<AnnouncementFixedShapeSurface, string> = {
  canvasChatComposer: 'Shown as a notice attached to the top of the chat message box.',
  global: 'Shown as a full-width bar above the app.',
};

export const ANNOUNCEMENT_SURFACE_DESCRIPTIONS: Record<AnnouncementSurface, string> = {
  assetDetails: 'In the asset details view, when someone opens an asset.',
  boards: 'At the top of a board.',
  canvas: 'On an open canvas that already has content.',
  canvasChatComposer: 'Attached to the top of the message box in canvas chat.',
  canvasNullState: 'On a new, empty canvas.',
  global: 'As a bar above the app on every page in the workspace.',
  plans: 'On the workspace plan page, which only admins can open.',
};

/** Compact labels for tight layouts. */
export const ANNOUNCEMENT_SURFACE_SHORT_LABELS: Record<AnnouncementSurface, string> = {
  assetDetails: 'Asset details',
  boards: 'Boards',
  canvas: 'Canvas',
  canvasChatComposer: 'Canvas chat',
  canvasNullState: 'Empty canvas',
  global: 'Every page',
  plans: 'Plans',
};

/** Surfaces that show an announcement's media. The every-page bar, boards and plans show the text without it. */
export const ANNOUNCEMENT_MEDIA_SURFACES: readonly AnnouncementSurface[] = [
  'assetDetails',
  'canvas',
  'canvasNullState',
  'canvasChatComposer',
];

export const ANNOUNCEMENT_VARIANTS = ['pill', 'banner'] as const;

export type AnnouncementVariant = (typeof ANNOUNCEMENT_VARIANTS)[number];

export const ANNOUNCEMENT_TONES = ['neutral', 'info', 'warning', 'error'] as const;

export type AnnouncementTone = (typeof ANNOUNCEMENT_TONES)[number];

export const ANNOUNCEMENT_MEDIA_TYPES = ['image', 'video'] as const;

export type AnnouncementMediaType = (typeof ANNOUNCEMENT_MEDIA_TYPES)[number];

export const ANNOUNCEMENT_ACTION_TYPES = ['button', 'link'] as const;

export type AnnouncementActionType = (typeof ANNOUNCEMENT_ACTION_TYPES)[number];

export const ANNOUNCEMENT_COMMANDS = {
  openAgent:
    'Opens the workspace agent in the current tab. An optional `prompt` starts a new conversation and sends it. Works on the every-page banner, boards, and asset details, wherever the reader can use the agent.',
  openAgentModelPicker:
    'Opens the canvas agent model picker and turns auto off. Preselects the model in `modelName` when given and available. Works on canvas, an empty canvas, and canvas chat.',
  openAssetPicker: 'Opens the asset picker on the current canvas. Works on canvas and on an empty canvas.',
  openBlankCanvas:
    'Opens a new empty canvas. Works on the every-page banner, boards, plans, news, and asset details, wherever the reader can create a canvas.',
  openCanvasAgent:
    'Opens the canvas agent chat on the current canvas. An optional `prompt` is sent to it. If the agent is mid-reply or the reader has an unsent draft, the prompt is left in the message box instead of sent. Works on canvas and on an empty canvas.',
  openInCanvas: 'Opens the asset the reader is viewing in a new canvas. Works on asset details.',
  selectAgentModel:
    'Switches the canvas agent to the model the action names, and turns auto off. Works on canvas, an empty canvas, and canvas chat.',
} as const;

export type AnnouncementCommand = keyof typeof ANNOUNCEMENT_COMMANDS;

/**
 * Surfaces whose host can run each command. Hosts may still hide a command at runtime, e.g. when the
 * reader cannot use the agent; a command action on any other surface never renders.
 */
export const ANNOUNCEMENT_COMMAND_SURFACES: Record<AnnouncementCommand, readonly AnnouncementSurface[]> = {
  openAgent: ['global', 'boards', 'assetDetails'],
  openAgentModelPicker: ['canvas', 'canvasNullState', 'canvasChatComposer'],
  openAssetPicker: ['canvas', 'canvasNullState'],
  openBlankCanvas: ['global', 'boards', 'plans', 'assetDetails'],
  openCanvasAgent: ['canvas', 'canvasNullState'],
  openInCanvas: ['assetDetails'],
  selectAgentModel: ['canvas', 'canvasNullState', 'canvasChatComposer'],
};

export const getAnnouncementSurfaceCommands = (surface: AnnouncementSurface): AnnouncementCommand[] => {
  return Object.entries(ANNOUNCEMENT_COMMAND_SURFACES).flatMap(([command, surfaces]) =>
    isAnnouncementCommand(command) && surfaces.includes(surface) ? [command] : [],
  );
};

const ANNOUNCEMENT_SURFACE_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_SURFACES);
const ANNOUNCEMENT_FIXED_SHAPE_SURFACE_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_FIXED_SHAPE_SURFACES);
const ANNOUNCEMENT_MEDIA_SURFACE_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_MEDIA_SURFACES);
const ANNOUNCEMENT_VARIANT_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_VARIANTS);
const ANNOUNCEMENT_TONE_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_TONES);
const ANNOUNCEMENT_MEDIA_TYPE_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_MEDIA_TYPES);
const ANNOUNCEMENT_ACTION_TYPE_SET: ReadonlySet<string> = new Set(ANNOUNCEMENT_ACTION_TYPES);
const ANNOUNCEMENT_COMMAND_SET: ReadonlySet<string> = new Set(Object.keys(ANNOUNCEMENT_COMMANDS));

export const isAnnouncementSurface = (value: string): value is AnnouncementSurface => {
  return ANNOUNCEMENT_SURFACE_SET.has(value);
};

export const isAnnouncementFixedShapeSurface = (
  surface: AnnouncementSurface,
): surface is AnnouncementFixedShapeSurface => {
  return ANNOUNCEMENT_FIXED_SHAPE_SURFACE_SET.has(surface);
};

export const isAnnouncementMediaSurface = (surface: AnnouncementSurface): boolean => {
  return ANNOUNCEMENT_MEDIA_SURFACE_SET.has(surface);
};

export const isAnnouncementVariant = (value: string): value is AnnouncementVariant => {
  return ANNOUNCEMENT_VARIANT_SET.has(value);
};

export const isAnnouncementTone = (value: string): value is AnnouncementTone => {
  return ANNOUNCEMENT_TONE_SET.has(value);
};

export const isAnnouncementMediaType = (value: string): value is AnnouncementMediaType => {
  return ANNOUNCEMENT_MEDIA_TYPE_SET.has(value);
};

export const isAnnouncementActionType = (value: string): value is AnnouncementActionType => {
  return ANNOUNCEMENT_ACTION_TYPE_SET.has(value);
};

export const isAnnouncementCommand = (value: string): value is AnnouncementCommand => {
  return ANNOUNCEMENT_COMMAND_SET.has(value);
};
